import { useQuery } from "@msuite/ui/query";
import { guardArray } from "@msuite/utils/array";
import { getFileBatch } from "@/api/files";

/**
 * What became of every file of the drops one person is watching.
 *
 * Fetched rather than synced, unlike the pill's counts, and the split is the same one the inbox
 * makes: the pill follows a batch live because a worker moves it without the browser asking, while
 * this is a report somebody opens and reads. It also carries what the sync path deliberately does
 * not — the reason, the candidates and the resolved destination — because those are the inbox's
 * columns rather than the pill's, and the wire shape says so.
 *
 * More than one batch, because the pill reports one figure over every drop still in flight and the
 * report has to cover the same set.
 *
 * @param batchIds - The upload ids to report on.
 * @param isEnabled - Whether to ask at all; the report is only fetched once it is opened.
 * @returns The batches' files, flattened and in drop order.
 */
export function useFileBatches(batchIds: string[], isEnabled: boolean) {
	/** Query */
	const query = useQuery({
		queryKey: ["file-batches", ...batchIds],
		queryFn: async () => {
			const batches = await Promise.all(batchIds.map((batchId) => getFileBatch(batchId)));
			return batches.flat();
		},
		enabled: isEnabled && batchIds.length > 0,
	});

	/** Return */
	return { ...query, files: guardArray(query.data) };
}
